import { User } from '../types';

interface LoginCredentials {
  username: string;
  password: string;
  twoFactorCode?: string;
}

interface AuthResponse {
  success: boolean;
  user?: User;
  error?: string;
  requiresTwoFactor?: boolean;
}

class AuthService {
  private readonly SESSION_KEY = 'bpp_session';
  private readonly SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Input validation
    if (!this.validateCredentials(credentials)) {
      return { success: false, error: 'Invalid credentials format' };
    }

    // Sanitize inputs
    const sanitizedUsername = this.sanitizeInput(credentials.username);
    const sanitizedPassword = credentials.password; // Don't log or expose password

    try {
      // In a real implementation, this would call a secure backend API
      // For demo purposes, we maintain one hardcoded admin but with better security
      if (sanitizedUsername === 'admin' && credentials.password === 'admin') {
        // Simulate 2FA requirement
        if (!credentials.twoFactorCode) {
          return { 
            success: false, 
            requiresTwoFactor: true,
            error: 'Two-factor authentication required' 
          };
        }

        // Validate 2FA code (in real app, this would be validated server-side)
        if (credentials.twoFactorCode !== '123456') {
          return { success: false, error: 'Invalid two-factor code' };
        }

        const user: User = {
          id: 'super-admin-001',
          username: 'admin',
          email: 'admin@bpp.gov.ng',
          role: 'super_admin',
          permissions: [
            'view_all_tenders',
            'manage_users',
            'approve_contracts',
            'system_settings',
            'audit_logs',
            'manage_suppliers',
            'financial_oversight',
            'pricing_standards',
            'submit_requisitions'
          ],
          status: 'active',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        };

        console.log('Admin user logged in with permissions:', user.permissions);
        this.createSecureSession(user);
        return { success: true, user };
      }

      // Add rate limiting simulation
      await this.simulateDelay();
      return { success: false, error: 'Invalid username or password' };

    } catch (error) {
      console.error('Authentication error occurred');
      return { success: false, error: 'Authentication service unavailable' };
    }
  }

  private validateCredentials(credentials: LoginCredentials): boolean {
    if (!credentials.username || !credentials.password) {
      return false;
    }

    // Username validation
    if (credentials.username.length < 3 || credentials.username.length > 50) {
      return false;
    }

    // Password validation
    if (credentials.password.length < 4 || credentials.password.length > 128) {
      return false;
    }

    // Check for malicious patterns
    const maliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+=/i,
      /\beval\b/i,
      /\balert\b/i
    ];

    const inputString = credentials.username + credentials.password;
    return !maliciousPatterns.some(pattern => pattern.test(inputString));
  }

  private sanitizeInput(input: string): string {
    return input
      .trim()
      .replace(/[<>\"'&]/g, '')
      .substring(0, 50);
  }

  private createSecureSession(user: User): void {
    const sessionData = {
      user,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.SESSION_TIMEOUT,
      sessionId: this.generateSessionId()
    };

    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.error('Failed to create session');
      throw new Error('Session creation failed');
    }
  }

  getStoredUser(): User | null {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      if (!sessionData) return null;

      const session = JSON.parse(sessionData);
      
      // Check session expiry
      if (Date.now() > session.expiresAt) {
        this.logout();
        return null;
      }

      // Extend session on activity
      this.extendSession(session);
      
      return session.user;
    } catch (error) {
      console.error('Failed to retrieve session');
      this.logout();
      return null;
    }
  }

  private extendSession(session: any): void {
    session.expiresAt = Date.now() + this.SESSION_TIMEOUT;
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
  }

  logout(): void {
    try {
      localStorage.removeItem(this.SESSION_KEY);
    } catch (error) {
      console.error('Failed to clear session');
    }
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private async simulateDelay(): Promise<void> {
    // Simulate network delay to prevent timing attacks
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
  }

  hasPermission(user: User | null, permission: string): boolean {
    if (!user || !user.permissions) {
      console.log(`No user or permissions found for permission check: ${permission}`);
      return false;
    }
    
    const hasAccess = user.permissions.includes(permission);
    console.log(`Permission check: ${permission} for user: ${user.username} - Result: ${hasAccess}`);
    console.log(`Available permissions:`, user.permissions);
    return hasAccess;
  }

  canAccessModule(user: User | null, module: string): boolean {
    if (!user) {
      console.log(`No user found for module access check: ${module}`);
      return false;
    }

    const modulePermissions: Record<string, string[]> = {
      'users': ['manage_users'],
      'suppliers': ['manage_suppliers'],
      'prices': ['view_all_tenders'],
      'labour-database': ['view_all_tenders', 'pricing_standards'],
      'price-schedule': ['view_all_tenders'],
      'historical-prices': ['view_all_tenders'],
      'regional-factors': ['view_all_tenders'],
      'price-trends': ['view_all_tenders'],
      'pricing-standard': ['view_all_tenders', 'pricing_standards'],
      'rate-calculator': ['view_all_tenders', 'pricing_standards'],
      'cost-model': ['view_all_tenders', 'pricing_standards'],
      'cost-model-overview': ['view_all_tenders', 'pricing_standards'],
      'cost-model-components': ['view_all_tenders', 'pricing_standards'],
      'cost-model-subcomponents': ['view_all_tenders', 'pricing_standards'],
      'cost-model-geozones': ['view_all_tenders', 'pricing_standards'],
      'cost-model-models': ['view_all_tenders', 'pricing_standards'],
      'bill-of-quantities': ['view_all_tenders', 'pricing_standards'],
      'audit': ['audit_logs']
    };

    const requiredPermissions = modulePermissions[module] || [];
    console.log(`Module access check: ${module} for user: ${user.username}`);
    console.log(`Required permissions for ${module}:`, requiredPermissions);
    console.log(`User permissions:`, user.permissions);
    
    const hasAccess = requiredPermissions.some(permission => this.hasPermission(user, permission));
    console.log(`Module ${module} access result: ${hasAccess}`);
    
    return hasAccess;
  }
}

export const authService = new AuthService();
